import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useBackgroundLibrary } from "@/components/Backgrounds/useBackgroundLibrary";
import { useBackgroundRequests } from "@/components/Backgrounds/useBackgroundRequests";
import { PageWrapper } from "@/components/PageWrapper";
import { AdminDesignStudioHeader } from "@/containers/Admin/DesignStudio/AdminDesignStudioHeader";
import { useDesignStudioScope } from "@/containers/Coach/DesignStudio/DesignStudioScope";
import { LibraryGenerateDialog } from "@/components/Backgrounds/library/LibraryGenerateDialog";
import { AssetGrid, AssetSelection } from "@/components/Backgrounds/library/AssetGrid";
import { AssetReviewDialog } from "./AssetReviewDialog";
import {
  LibraryActions,
  LibraryScope,
  LibraryToolbar,
  librarySearchBody,
} from "@/components/Backgrounds/library/LibraryToolbar";
import { QueueStrip } from "./QueueStrip";
import { SelectionBar } from "@/components/Backgrounds/library/SelectionBar";
import { useLibraryView } from "@/components/Backgrounds/library/useLibraryView";
import { useLibraryUpload } from "@/components/Backgrounds/library/useLibraryUpload";

const UPLOAD_NODE = "design-backgrounds/global";
const ASSET_PARAM = "asset";

function assetFromSearch(search: string): number | null {
  const id = Number(new URLSearchParams(search).get(ASSET_PARAM));
  return Number.isInteger(id) && id > 0 ? id : null;
}

interface GenerateState {
  open: boolean;
  prompt?: string;
  scope: Exclude<LibraryScope, "all">;
}

/** Super-admin curation of the shared and per-coach background libraries. */
export const BackgroundLibraryPage: React.FC = () => {
  // Shared with the Designs tab, so switching tabs keeps the coach.
  const { scope, coach } = useDesignStudioScope();
  // Filters live in the URL beside ?asset=; the scope stays in the shared in-memory store.
  const { filters, update } = useLibraryView("admin");
  const [selected, setSelected] = React.useState<AssetSelection>(new Map());
  const [generate, setGenerate] = React.useState<GenerateState>({ open: false, scope: "global" });

  const history = useHistory();
  const location = useLocation();
  // The open asset lives in the URL so a link or a reload lands on it.
  const openAssetId = assetFromSearch(location.search);
  const setOpenAssetId = (id: number | null) => {
    // The live location: an upload can finish after the filters have changed.
    const params = new URLSearchParams(history.location.search);
    if (id == null) params.delete(ASSET_PARAM);
    else params.set(ASSET_PARAM, String(id));
    const search = params.toString();
    history.replace({ ...history.location, search: search ? `?${search}` : "" });
  };

  const body = librarySearchBody(filters, scope, coach);
  // The grid's own query (same key, so no extra request), for paging through it in the review view.
  const { assets, matches, isFetching } = useBackgroundLibrary(body);
  const openIndex = openAssetId == null ? -1 : assets.findIndex(asset => asset.id === openAssetId);
  const { pending } = useBackgroundRequests({ scope: "all", coachId: scope === "coach" ? coach?.id : undefined });
  const pendingNew = pending.filter(request => request.backgroundAssetId === null);

  // A selection belongs to one library.
  React.useEffect(() => setSelected(new Map()), [scope, coach?.id]);

  const { upload, uploading } = useLibraryUpload({
    node: UPLOAD_NODE,
    request: url => (scope === "global" ? { url, scope: "global", visibility: "draft" } : { url, coachId: coach!.id }),
    onUploaded: asset => setOpenAssetId(asset.id),
  });

  return (
    <PageWrapper hideSeparator fitViewport className="flex min-h-0 flex-1 flex-col gap-0">
      <AdminDesignStudioHeader
        tab="backgrounds"
        actions={
          <LibraryActions
            canWrite={scope === "global" || coach !== null}
            onGenerate={() => setGenerate({ open: true, scope })}
            onUpload={files => void upload(files.slice(0, 1))}
            uploading={uploading}
          />
        }
      />
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-6" data-testid="background-library">
        <LibraryToolbar
          mode="admin"
          filters={filters}
          onFiltersChange={next => update({ filters: next })}
          resultCount={isFetching ? null : matches}
        />
        <QueueStrip />
        <AssetGrid
          mode="admin"
          body={body}
          previewRatio={filters.previewRatio}
          selected={selected}
          onSelectedChange={setSelected}
          onOpen={setOpenAssetId}
          pendingRequests={pendingNew}
          empty="Nothing here. Create some with AI or change the filters."
        />
        <SelectionBar mode="admin" selected={selected} scope={scope} onSelectedChange={setSelected} />
      </div>

      <AssetReviewDialog
        assetId={openAssetId}
        onClose={() => setOpenAssetId(null)}
        onOpenAsset={setOpenAssetId}
        onNewFromPrompt={prompt => setGenerate({ open: true, prompt, scope: "global" })}
        previousId={openIndex > 0 ? assets[openIndex - 1].id : null}
        nextId={openIndex >= 0 && openIndex < assets.length - 1 ? assets[openIndex + 1].id : null}
        position={openIndex >= 0 ? { index: openIndex + 1, total: assets.length } : null}
      />
      <LibraryGenerateDialog
        mode="admin"
        open={generate.open}
        onOpenChange={open => setGenerate(current => ({ ...current, open }))}
        coach={coach}
        scope={generate.scope}
        initialPrompt={generate.prompt}
      />
    </PageWrapper>
  );
};
